/**
 * Asking a place what has happened to it.
 *
 * `sim/godhood/bindings.ts` has carried this line since the Web was built:
 *
 *     'place.sense.state': absent('a place cannot be asked what has happened to it')
 *
 * That was true, and it was the largest hole in the game's own premise. The
 * world knows a great deal — the shipped century carries 39,633 burned cells in
 * 574 scars, ten of them over a hundred hectares and aged ten to seventy-one
 * years, 191 structures of which 68 are ruins, 33 ways worn mostly to roads, 18
 * camps left to the land and twelve peoples — and until now the whole of what a
 * player could learn by pointing at any of it was one clause: *a shelter —
 * fallen in, and nobody has mended it*. True, and historyless. **The
 * simulation knew a hundred years and could not say a sentence about them.**
 *
 * This is the read that says them. It invents nothing and stores nothing: every
 * note below is assembled from a store that already exists, already persists,
 * and was already being written for its own reasons.
 *
 * ## Three laws shape it, and they are why it is built this way
 *
 * **Law 9 — OBSERVATION MUST NOT CREATE HISTORY.** Nothing here takes a writer.
 * `LoreSource` is a structural type of *readers only*, so a lore call cannot
 * write even by accident, and `test/lore.ts` reads every interesting place in a
 * live world and asserts the encoded snapshot is byte-identical afterwards —
 * the same tripwire `test/attention.ts` puts under hovering.
 *
 * **Law 10 — TRUE CAUSE ≠ PERCEIVED CAUSE.** `Mark.by` is a `godId` and lives
 * behind the wall `test/deity.ts` enforces. It is dropped here rather than
 * carried and filtered later, because the honest place to drop a thing is the
 * first place that touches it. What survives of a divine mark is
 * `unexplained` — a witness knows the ground was changed and nothing whatever
 * about a hand. The one exception is deliberate and is not a leak: a god may be
 * told about **its own** deeds, from strands filtered to its own id, and those
 * come back in a separate register (`yours`) that no mortal-facing surface may
 * mix with the world's own voice.
 *
 * **The server will own history.** So this returns structured notes and not
 * prose. `ui/lore.ts` turns them into sentences, exactly as `ui/hud.ts`'s
 * `phrase()` turns a `PerceivedEvent` into one. Nothing under `src/sim/` should
 * ever contain a sentence a player reads.
 *
 * ## Two sources, and which one answers depends on when
 *
 * Measured with `tools/lore.ts` against the shipped `public/prehistory.json`:
 * **it carries no chronicle section at all** — 0 marks, 0 strands, 0 promoted
 * things, 0 anchors. The file was generated on 2026-08-13 and `chronicle.ts`
 * landed on 2026-08-14, so the world the player is dropped into has a hundred
 * years of history and none of it in the durable record.
 *
 * That is not a defect to route around, it is the shape of the thing:
 *
 *  · **Before the player arrives**, the readable history is the SERE and the
 *    BAND stores — 39,633 burned cells in 574 scars, 191 structures of which 68
 *    are ruins, 33 ways. This is what makes the island feel older than the god.
 *  · **After the player arrives**, the CHRONICLE is the source, because from
 *    that moment every durable change is folded into a mark.
 *
 * So a reading must never depend on the Chronicle alone, and `test/lore.ts`
 * holds that: it asserts a world with an empty Chronicle still speaks. Whoever
 * next rebuilds `prehistory.json` (`tools/worldgen.ts`, ~20 minutes) will shift
 * the balance toward marks without changing the rule.
 *
 * One inherited imprecision worth knowing, since it is visible here: a `Mark`'s
 * `x`/`z` are the coordinates of the FIRST event folded into its 1,040 m cell
 * and never move, so a mark can be reported up to ~1.5 km from where the thing
 * it describes actually happened. `MARK_NEAR` is that same cell for that
 * reason — a mark is a *neighbourhood* statement and must never be phrased as
 * though it were standing here.
 *
 * Pure by law 7: no three, no `Math.random`, no wall clock. `now` is passed in.
 * Every horizon is in WORLD-seconds, so nothing here moves when the clock does.
 */

import type { EventType, PerceivedCause } from './events';
import type { Mark, Strand } from './chronicle';
import { MARK_CELL } from './chronicle';
import type { AnchorState, SacredStage } from './anchor';
import type { PathRecord, StructureState } from './mortals/types';
import type { SereStage } from '@/world/sere';
import { PROMOTE, type ThingMark } from '@/world/things';

/* ── how far each question reaches ─────────────────────────────────────── */

/**
 * Every radius below is a constant the world already chose for its own
 * reasons, imported or restated rather than invented. A reading that used its
 * own numbers would drift out of agreement with the systems it reads, and the
 * drift would be silent.
 */

/**
 * A camp, in metres. `render/habitation.ts` clusters structures into camps at
 * `clearingRadius = max(52, spread + 42)`; 120 m is that floor with room for
 * the spread, and it is the same order as the 150 m the far-field camp finder
 * uses. What it must NOT be is the mark cell — a hearth a kilometre away is a
 * different place, and folding it in here would tell you about somebody else's
 * home.
 */
export const CAMP_NEAR = 120;

/**
 * A way passing close enough to be *this* place's way. The picker's own
 * tolerance for pointing at a trail is 5 m, which is what it takes to mean the
 * path itself; this is what it takes for the path to be part of the story of
 * the ground beside it.
 */
export const PATH_NEAR = 60;

/**
 * A stone. `anchor.ts`'s own `NOTICE_RANGE` — what a mortal happens to walk
 * past — restated rather than imported so `src/sim/lore.ts` does not acquire a
 * dependency on the anchor system to read a number.
 */
export const STONE_NEAR = 130;

/**
 * The neighbourhood a durable change belongs to. Exactly `MARK_CELL`, because
 * that is the grid the marks are folded onto: asking a finer question than the
 * store answers would report a burn as being nearer than the record knows.
 */
export const MARK_NEAR = MARK_CELL;

/**
 * How many notes a reading may carry. A cap rather than a filter, applied
 * *after* ordering, so what is dropped is always the least old — the oldest
 * things are what the player cannot find out any other way.
 */
export const MAX_NOTES = 14;

/* ── how far a reading reaches, and why it is not the picker's number ──── */

/**
 * `D §3.4`: SCALE CHANGES WHAT THE INTERFACE CAN RESOLVE. A reading taken from
 * thirty metres is about a spot; one taken from three kilometres is about a
 * neighbourhood, because from three kilometres the gaze cannot be aimed finer
 * than one.
 *
 * The obvious move was to borrow the picker's own tolerance — `far * 0.010` in
 * `main.ts` — and it is **measurably wrong**, which is the whole argument for
 * the project's measure-before-tuning rule. `tools/lore.ts`, on 120 gazes
 * landing 370 m from a camp in the shipped century:
 *
 *     reach     0 m    30 m   120 m   300 m   400 m   800 m
 *     silent   57.5%   57.5%   25.8%   10.0%    0.0%    0.0%
 *     notes p50   0       0       1       2       2       2
 *
 * The picker's factor puts 30 m of reach under a god three kilometres up and
 * changes **nothing at all** — 57.5% of the time the panel stays silent over
 * the most storied ground on the island, exactly during the EXPLORE part of
 * the loop. 0.12 puts ~360 m under the same god, which is the scale the gaze
 * is actually accurate to.
 *
 * The cap is half `MARK_CELL`. A camp eight hundred metres off is not this
 * place, and the Chronicle's own kilometre is the world's answer to how big a
 * neighbourhood is; half of it is as far as a reading may claim something is
 * *here*. (Note the table's own warning: past 400 m the silence is already
 * zero and nothing improves, so reaching further buys inaccuracy for free.)
 *
 * Below about a kilometre of distance this does nothing — `0.12 * 1000` is
 * still under `CAMP_NEAR` — so the rule reads simply: **under a kilometre you
 * read a place, over it you read a neighbourhood.**
 */
export const REACH_FRACTION = 0.12;
export const REACH_MAX = MARK_CELL / 2;

/** How wide a reading taken from `far` metres away should be, in metres. */
export const reachFor = (far: number): number =>
  Math.min(REACH_MAX, Math.max(0, far) * REACH_FRACTION);

/* ── what the world can say ────────────────────────────────────────────── */

/**
 * One thing the world can be asked about this place, as data.
 *
 * `at` is world-seconds and is what orders a reading. Every variant carries
 * only what the sentence needs; nothing carries a `godId`, and there is no
 * variant that could.
 */
export type Note =
  /**
   * Fire came through, and what has grown back since. `spread` is how far the
   * same burn reaches from here, in metres — measured, not stored, because the
   * difference between "a fire" and "the fire that took this valley" is the
   * whole of why a burn is worth pointing at, and the sere keeps cells rather
   * than fires. See `burnSpread`.
   */
  | { kind: 'burned'; at: number; years: number; stage: SereStage; spread: number }
  /**
   * The strongest sentence in the file, and it is one subtraction: this thing
   * was already apart from the others when the fire came, and it is still here.
   * The sere's own age against the mark's `since` — no new state at all.
   */
  | { kind: 'stood-through-fire'; at: number; years: number }
  /** This thing stopped being ordinary. `tended` is the sign of its standing. */
  | { kind: 'apart'; at: number; tended: boolean; acts: number }
  /** …and its standing has decayed most of the way back toward ordinary. */
  | { kind: 'forgetting'; at: number; tended: boolean }
  /** People built here. `since` is the oldest roof still standing as evidence. */
  | { kind: 'settled'; at: number; roofs: number; people?: string }
  /** …and nobody has mended any of them. */
  | { kind: 'abandoned'; at: number; roofs: number }
  /** A way runs past. `strength` is the path's own 0..1. */
  | { kind: 'walked'; at: number; strength: number; lastUsedAt: number }
  /** A god came down and bound itself here. Whose is never carried. */
  | { kind: 'stone'; at: number; stage: SacredStage; visits: number }
  /**
   * A durable change the Chronicle folded onto this neighbourhood.
   *
   * The cause is a `PerceivedCause`, never a `godId` and never the boolean the
   * Mark carries. That is law 10 made **structural rather than careful**: the
   * type cannot express *a god did this*, only *natural* or *unexplained*, so
   * the leak is not something a later edit can reintroduce by forgetting. It
   * is the same collapse `perceive()` performs on an event, applied to the
   * durable record, and `Mark.by` — which is a god id behind `test/deity.ts`'s
   * wall — is dropped at the first line that touches it rather than filtered
   * downstream.
   *
   * `at` is the mark's LAST fold and `first` is its first. That way round
   * because a mark is the only note that reports a *repeated* thing, and the
   * time it happened is the last time it happened — which is also exactly what
   * `Chronicle.marksNear` filters a `since` against, so a return reading cannot
   * come back carrying a note dated before the player left.
   */
  | { kind: 'mark'; at: number; first: number; type: EventType;
      count: number; cause: PerceivedCause }
  /**
   * Your own hand, from your own strand. Never mixed with the world's voice.
   * `at` is the most recent act and `first` the earliest, for the same reason
   * a mark carries both: a strand is a relationship rather than a moment.
   */
  | { kind: 'yours'; at: number; first: number; verb: string; acts: number }
  /** …and the world confirmed something of it stayed. */
  | { kind: 'endured'; at: number; verb: string; left: readonly string[] };

/**
 * A reading of one place, or of one thing standing in it.
 *
 * Two registers, and the split is the wall. `world` is what anyone standing
 * here could come to know and is safe to show beside a mortal's own words;
 * `yours` is the god's private record of its own deeds, read from strands
 * filtered to its own id, and must never be attributed to the world.
 */
export interface Reading {
  world: Note[];
  yours: Note[];
}

/** True where the world has nothing at all to say — the common case, and cheap. */
export const isSilent = (r: Reading): boolean =>
  r.world.length === 0 && r.yours.length === 0;

/* ── what a reading is allowed to see ──────────────────────────────────── */

/**
 * The world, as narrowly as a reading needs it — and **readers only**.
 *
 * Structurally typed rather than taking the `Simulation`, for the two reasons
 * `godhood/oracle.ts` gives for doing the same: `test/lore.ts` can pass a
 * hand-built world, and nothing here can quietly grow a dependency on the
 * renderer or the clock. The stronger reason is law 9 — a type that offers no
 * `mark`, `stamp` or `emit` cannot write, so "looking writes nothing" is a
 * property of the signature rather than a promise in a comment.
 */
export interface LoreSource {
  sere: {
    ageAt(x: number, z: number, now: number): number | null;
    stageAt(x: number, z: number, now: number): SereStage | null;
  };
  things: { at(x: number, z: number, now: number): ThingMark | null };
  chronicle: {
    marksNear(x: number, z: number, radius: number, since?: number): Mark[];
    all(): readonly Strand[];
  };
  anchors: { near(x: number, z: number, radius: number): AnchorState[] };
  bands: {
    structures(): readonly StructureState[];
    paths(): readonly PathRecord[];
  };
}

/** A ruin, by `render/structures.ts`'s own threshold. */
const RUIN = 0.3;

/**
 * How far standing may fall toward the promotion threshold before the world is
 * said to be forgetting. `things.ts` promotes at 0.55 and forgets entirely at
 * 0.02; a thing within a tenth of the threshold, from above, is one that is
 * on its way back to being a tree.
 */
const FADING = 0.10;

const near = (ax: number, az: number, bx: number, bz: number, r: number): boolean =>
  Math.hypot(ax - bx, az - bz) <= r;

/* ── how big the fire was ──────────────────────────────────────────────── */

/**
 * The `SereStore` keeps CELLS, not fires — one `lastBurnAt` per 32 m of ground,
 * which is the right state for succession and says nothing about how much
 * burned at once. But the difference between *a fire came through* and *the
 * fire that took this valley* is the whole reason a scar is worth pointing at,
 * and the shipped century's ten biggest scars run 106–1,074 ha. Measured here
 * rather than stored, because storing it would be a second copy of something
 * the sere already implies, and law 2 says only divergence is kept.
 *
 * Eight rays, stepping one cell at a time, stopping where the ground either
 * never burned or burned at a different time. `SAME_FIRE` is a month, which is
 * `tools/lore.ts`'s own flood-fill join — a front takes days to cross a
 * hillside and a reburn is measured (`tools/sere.ts`) at a p50 gap of 43.8
 * years, so a month separates them by three orders of magnitude and no
 * threshold in between would behave differently.
 *
 * `RAY_STEPS` caps a ray at 2 km, so the worst case is 8 × 64 = 512 map reads
 * however large the scar; `tools/lore.ts` prices the whole reading.
 */
const SAME_FIRE = 30 * 86400;
const RAY_STEPS = 64;
const RAY_STEP_M = 32;   // SCAR_CELL — the sere's own lattice

function burnSpread(
  sere: LoreSource['sere'], x: number, z: number, now: number, burnedAt: number,
): number {
  const YEAR = 365 * 86400;
  let total = 0;
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const dx = Math.cos(a) * RAY_STEP_M, dz = Math.sin(a) * RAY_STEP_M;
    let reach = 0;
    for (let s = 1; s <= RAY_STEPS; s++) {
      const years = sere.ageAt(x + dx * s, z + dz * s, now);
      if (years === null) break;
      if (Math.abs((now - years * YEAR) - burnedAt) > SAME_FIRE) break;
      reach = s * RAY_STEP_M;
    }
    total += reach;
  }
  // the mean ray doubled: a diameter rather than a radius, which is the number
  // a person would say aloud about a burn they were standing in
  return (total / 8) * 2;
}

/** Closest approach of a walked polyline to a point, in metres. */
function pathDistance(p: PathRecord, x: number, z: number): number {
  let best = Infinity;
  for (const pt of p.points) best = Math.min(best, Math.hypot(pt.x - x, pt.z - z));
  return best;
}

/* ── the read ──────────────────────────────────────────────────────────── */

/**
 * Everything the world can be asked about one place, oldest first.
 *
 * `subject` is the target's id where the pointer resolved a thing — the same
 * `plant:<placeKey>` string `sim.act` writes into an event as `targetId`, which
 * is what makes a strand *this tree's* rather than this hillside's. Omit it and
 * the reading is of the ground.
 *
 * `godId` opts into the second register. Omit it and `yours` is empty, which is
 * what a reading taken on behalf of anybody but the player should do.
 *
 * `since` filters both registers to what happened at or after a world-time —
 * the whole of "what changed while I was gone", for free, because every note
 * already carries when it was. `Chronicle.marksNear` has taken a `since`
 * argument since the day it was written and nothing had ever passed one.
 */
export function readAt(
  w: LoreSource,
  x: number, z: number,
  now: number,
  opts: { subject?: string; godId?: string; since?: number; reach?: number } = {},
): Reading {
  const since = opts.since ?? -Infinity;
  // How big a place this reading is about. `D §3.4`: scale changes what the
  // interface can resolve — a reading taken from thirty metres is about a
  // spot, and one taken from three kilometres is about a neighbourhood.
  //
  // Measured, and it is not a nicety: at 3 km the gaze lands within 120 m of a
  // structure so rarely that the panel went silent over the most storied
  // ground on the island, which kills the EXPLORE → NOTICE beat the whole
  // milestone rests on. The caller passes the SAME number the picker already
  // uses for its own tolerance (`far * 0.010` in `main.ts`), so what a god can
  // point at and what the world will tell them about grow together instead of
  // drifting apart.
  //
  // `MARK_NEAR` deliberately does not scale: it is the grid the Chronicle
  // folds onto, and asking wider than the store resolves would report a burn
  // from the next valley as though it were this one.
  const reach = Math.max(0, opts.reach ?? 0);
  const campNear = Math.max(CAMP_NEAR, reach);
  const pathNear = Math.max(PATH_NEAR, reach);
  const stoneNear = Math.max(STONE_NEAR, reach);
  const world: Note[] = [];
  const yours: Note[] = [];

  /* -- the ground's own memory of fire -------------------------------- */
  const years = w.sere.ageAt(x, z, now);
  const stage = w.sere.stageAt(x, z, now);
  const burnedAt = years === null ? null : now - years * 365 * 86400;
  if (years !== null && stage !== null && burnedAt !== null && burnedAt >= since) {
    world.push({ kind: 'burned', at: burnedAt, years, stage,
                 spread: burnSpread(w.sere, x, z, now, burnedAt) });
  }

  /* -- the thing itself, if history has made it one ------------------- */
  const mark = w.things.at(x, z, now);
  if (mark !== null) {
    const tended = mark.standing > 0;
    const magnitude = Math.abs(mark.standing);
    // `PROMOTE` is imported rather than restated. It is a tuned constant that
    // the project's memory says is parked and must not be adjusted, and a
    // second copy of a parked constant is the way one quietly gets adjusted.
    if (magnitude >= PROMOTE) {
      if (mark.since >= since) {
        world.push({ kind: 'apart', at: mark.since, tended, acts: mark.acts });
      }
      if (magnitude < PROMOTE + FADING && mark.at >= since) {
        world.push({ kind: 'forgetting', at: mark.at, tended });
      }
      // The one sentence that is worth the whole file. If the ground under it
      // burned AFTER it became important, then the transform chain spared it
      // (`world/sere.ts`'s `spare`) and it is standing in its own scar.
      if (burnedAt !== null && burnedAt > mark.since && burnedAt >= since) {
        world.push({ kind: 'stood-through-fire', at: burnedAt, years: years! });
      }
    }
  }

  /* -- what people built here, and whether they stayed ---------------- */
  let oldest = Infinity, roofs = 0, fallen = 0, lastMended = -Infinity;
  for (const s of w.bands.structures()) {
    if (!near(s.x, s.z, x, z, campNear)) continue;
    roofs++;
    oldest = Math.min(oldest, s.builtAt);
    lastMended = Math.max(lastMended, s.builtAt);
    if (s.condition < RUIN) fallen++;
  }
  if (roofs > 0 && oldest >= since) {
    world.push({ kind: 'settled', at: oldest, roofs });
  }
  // "left to the land" is every roof down, which is what `camp_abandoned`
  // means and what 68 of the shipped century's 191 structures are. Dated from
  // the newest thing anybody built, because that is the last time this was a
  // place somebody was keeping.
  if (roofs > 0 && fallen === roofs && lastMended >= since) {
    world.push({ kind: 'abandoned', at: lastMended, roofs });
  }

  /* -- what walks past ------------------------------------------------ */
  // ONE note, not one per path. Measured (`tools/lore.ts`): read with a 400 m
  // reach, a point near a camp touches five ways and reported all five —
  // "a road runs past here" five times over, which is a firehose wearing a
  // history's clothes. The world's own answer to this is folding, and the
  // strongest way is the one a person standing here would actually mention.
  let way: PathRecord | null = null;
  for (const p of w.bands.paths()) {
    if (pathDistance(p, x, z) > pathNear) continue;
    if (p.createdAt < since) continue;
    // strongest first, and the older of two equals — the one that has been
    // there longest is the one with the story
    if (!way || p.strength > way.strength ||
        (p.strength === way.strength && p.createdAt < way.createdAt)) way = p;
  }
  if (way) {
    world.push({ kind: 'walked', at: way.createdAt,
                 strength: way.strength, lastUsedAt: way.lastUsedAt });
  }

  /* -- and whether a god ever came down here -------------------------- */
  for (const a of w.anchors.near(x, z, stoneNear)) {
    if (a.placedAt < since) continue;
    // `a.godId` stops here. There is no field on the note for it.
    world.push({ kind: 'stone', at: a.placedAt, stage: a.stage, visits: a.visits });
  }

  /* -- the durable record of the neighbourhood ------------------------ */
  // `Mark.by` is dropped rather than carried: law 10's wall, at the first
  // place that touches it.
  for (const m of w.chronicle.marksNear(x, z, MARK_NEAR, since)) {
    world.push({ kind: 'mark', at: m.last, first: m.first, type: m.type, count: m.count,
                 // the collapse. `m.by` stops here; `m.divine` becomes the
                 // only thing a witness could honestly hold about it
                 cause: m.divine ? 'unexplained' : 'natural' });
  }

  /* -- and your own hand, which is a different register --------------- */
  if (opts.godId !== undefined) {
    for (const s of w.chronicle.all()) {
      if (s.godId !== opts.godId) continue;
      const mine = opts.subject !== undefined
        ? s.subject === opts.subject
        : near(s.x, s.z, x, z, MARK_NEAR);
      if (!mine) continue;
      if (s.last >= since) {
        yours.push({ kind: 'yours', at: s.last, first: s.first,
                     verb: s.verb, acts: s.acts });
      }
      if (s.enduredAt !== undefined && s.enduredAt >= since) {
        yours.push({ kind: 'endured', at: s.enduredAt, verb: s.verb, left: s.left });
      }
    }
  }

  // A promoted thing marks its own kilometre, so a reading of the tree itself
  // would say "nothing about this one has been ordinary since" and then, from
  // the Chronicle, "something here stopped being ordinary" — the neighbourhood
  // repeating the thing back at you. The specific statement wins; the mark is
  // only news when you are NOT standing on the thing it is about.
  //
  // The test is the STORE, not the surviving note: under a `since` filter the
  // specific note can be older than the window while the mark is newer, and
  // then the redundant half is the only half left — which is how a reading of
  // a tree you just consecrated came back saying only "something here stopped
  // being ordinary", the neighbourhood telling you about the thing you were
  // standing on.
  const onIt = mark !== null && Math.abs(mark.standing) >= PROMOTE;
  const kept = onIt
    ? world.filter((n) => !(n.kind === 'mark' && n.type === 'thing_promoted'))
    : world;

  // Oldest first — a reading is a chronicle, not a feed. Ties break on RANK
  // rather than on the kind's spelling, because two notes at the same
  // world-second still have an order a story would tell them in: the thing
  // happened, and then what it means. ('endured' sorting before 'yours' on the
  // alphabet put the consequence before the act.) An explicit rank is also
  // what makes the order identical on every machine for ever, which is the
  // class of thing law 1 refuses to leave to how the stores were walked.
  const RANK: Record<Note['kind'], number> = {
    settled: 0, walked: 1, burned: 2, abandoned: 3, mark: 4,
    yours: 5, apart: 6, endured: 7, 'stood-through-fire': 8,
    stone: 9, forgetting: 10,
  };
  const order = (a: Note, b: Note) => a.at - b.at || RANK[a.kind] - RANK[b.kind];
  kept.sort(order);
  yours.sort(order);
  const world_ = kept;
  // Trim the youngest, never the oldest: what a player cannot find out any
  // other way is what happened long ago.
  return { world: world_.slice(0, MAX_NOTES), yours: yours.slice(0, MAX_NOTES) };
}
